import { Button } from '@repo/videa-components';
import { useAtomValue } from 'jotai';

import { shouldAllowRecordingAtom } from '../../atoms';

import { AudioFileUpload } from './AudioFileUpload';

interface ConsentFlowProps {
  onConsentDenied: () => void;
  onStartProviderOnlyRecording: () => void;
  onStartRecording: () => void;
  onAudioFileSelected?: (file: File, blobUrl: string) => void;
  isPromptSelected: boolean;
  monitoringStream: MediaStream | null;
  mediaBlobUrl?: string;
  isSheetOpen: boolean;
  buttonStyling: string;
  isVoiceNotesConsentEnabled: boolean;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
}

export const ConsentFlow = ({
  onConsentDenied,
  onStartProviderOnlyRecording,
  onStartRecording,
  onAudioFileSelected,
  isPromptSelected,
  monitoringStream,
  mediaBlobUrl,
  isSheetOpen,
  buttonStyling,
  isVoiceNotesConsentEnabled,
  isAudioUploadEnabled = false,
  showDevTools = false,
}: ConsentFlowProps) => {
  const shouldAllowRecording = useAtomValue(shouldAllowRecordingAtom);

  const renderAudioUpload = () => {
    if (!isAudioUploadEnabled || !onAudioFileSelected || !showDevTools)
      return null;

    return (
      <div className="vai-mt-4 vai-pt-4 vai-border-t vai-border-gray-200 vai-w-full vai-flex vai-flex-col vai-items-center">
        <p className="vai-text-sm vai-text-gray-500 vai-mb-2">
          Or upload a pre-recorded audio file
        </p>
        <AudioFileUpload
          onFileSelected={onAudioFileSelected}
          isDisabled={!isPromptSelected}
          size={isSheetOpen ? 'default' : 'sm'}
        />
      </div>
    );
  };

  return (
    <div className="vai-flex vai-flex-col vai-items-center">
      {isVoiceNotesConsentEnabled ? (
        <>
          <div className="vai-flex vai-justify-center vai-items-center vai-bg-amber-50 vai-text-amber-600 vai-border vai-border-amber-100 vai-rounded-md vai-px-2 vai-py-1 vai-mb-6 ai-text-sm vai-w-full">
            <p className="vai-text-center">
              Obtain recording consent: <br />
              &quot;
              <span className="vai-font-bold">
                Can we record this visit for note taking only, so we can focus
                on you?&quot;
              </span>
            </p>
          </div>

          <div className="vai-flex vai-flex-row vai-gap-x-3">
            <Button
              onClick={onConsentDenied}
              variant={'sheet'}
              size={isSheetOpen ? 'default' : 'sm'}
            >
              Consent Denied
            </Button>

            <Button
              onClick={onStartProviderOnlyRecording}
              variant={'sheet'}
              size={isSheetOpen ? 'default' : 'sm'}
              disabled={
                !isPromptSelected || !monitoringStream || !shouldAllowRecording
              }
            >
              Provider Only Recording
            </Button>

            <Button
              onClick={onStartRecording}
              variant={mediaBlobUrl ? 'sheet' : 'default'}
              className={`${buttonStyling} hover:vai-ring-primary/50`}
              size={isSheetOpen ? 'default' : 'sm'}
              disabled={
                !isPromptSelected || !monitoringStream || !shouldAllowRecording
              }
            >
              {!monitoringStream
                ? 'Select Microphone First'
                : `Yes, Start Recording`}
            </Button>
          </div>
          {renderAudioUpload()}
        </>
      ) : (
        <>
          <Button
            onClick={onStartRecording}
            variant={mediaBlobUrl ? 'sheet' : 'default'}
            className={`${buttonStyling} hover:vai-ring-primary/50`}
            size={isSheetOpen ? 'default' : 'sm'}
            disabled={
              !isPromptSelected || !monitoringStream || !shouldAllowRecording
            }
          >
            {!monitoringStream ? 'Select Microphone First' : `Start Recording`}
          </Button>
          {renderAudioUpload()}
        </>
      )}
    </div>
  );
};
